import { useWatch } from 'react-hook-form';
import React, { useMemo } from 'react';
import type { InputTableCellParams, InputTableRowParams, RepeatRowsPayload } from '@/components/custom/erp';
import type { PasteResolverConfig, PhieuChiCellValue, PhieuChiTaxRow } from '../../../types';
import { useCRUD, useMauSoHoaDon, useTaxRate } from '@/hooks';
import { SelectedCellInfo } from '../hooks/useTaxRows';
import { InputTable } from '@/components/custom/erp';
import { copyCellValue } from '@/lib/table-handlers';
import type { TinhChatThue } from '@/types/schemas';
import { getTaxTableColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useTaxPaste } from '../hooks';
import ActionBar from './ActionBar';

interface TaxTabProps {
  formMode: FormMode;
  rows: PhieuChiTaxRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuChiCellValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<PhieuChiTaxRow>) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (configs: Record<string, PasteResolverConfig>) => void;
  isPasting?: boolean;
}

export const TaxTab: React.FC<TaxTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const { data: tinhChatThue } = useCRUD<TinhChatThue, Record<string, never>>({ endpoint: QUERY_KEYS.TINH_CHAT_THUE });
  const type = useWatch({ name: 'ma_ngv', defaultValue: '1' });
  const columns = useMemo(
    () => getTaxTableColumns(tinhChatThue, onCellValueChange, formMode),
    [tinhChatThue, onCellValueChange, formMode]
  );

  useTaxPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          ma_ngv={type}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
